/**
 * BulkPlacementDialog — asignar ubicación y/o rack a VARIOS CIs a la vez.
 *
 * Lo usan los listados con selección múltiple (`ListPage` con `selectable`).
 * La pantalla decide el `entity` (la clave que entiende
 * `POST /api/bulk/placement`) y si esos elementos admiten rack.
 *
 * Los dos desplegables empiezan en «Sin cambios»: lo que no se toca, no se
 * manda. «Sin ubicación» / «Sin rack» sí se mandan (null) y desasignan.
 *
 * El rack se elige SIEMPRE después de la ubicación: al cambiarla se recargan
 * sus racks y se limpia la elección anterior (un rack de otra sede no vale).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Dialog from './Dialog';
import LocationTreeSelect from './LocationTreeSelect';

const NO_CHANGE = '';
const NONE = '__none__';

export default function BulkPlacementDialog({
  companyId,
  entity,
  rows = [],
  supportsRack = true,
  onClose,
  onDone,
}) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [locations, setLocations] = useState([]);
  const [racks, setRacks] = useState([]);
  const [locationChoice, setLocationChoice] = useState(NO_CHANGE);
  const [rackChoice, setRackChoice] = useState(NO_CHANGE);
  const [loadingRacks, setLoadingRacks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /* Ubicaciones (solo activas: no se manda un equipo a una sala dada de baja). */
  useEffect(() => {
    let alive = true;
    api.getLocations('', companyId)
      .then((res) => { if (alive) setLocations(res?.data || []); })
      .catch(() => { if (alive) setLocations([]); });
    return () => { alive = false; };
  }, [companyId]);

  /* Racks de la ubicación elegida. */
  useEffect(() => {
    const locationId = Number(locationChoice);
    if (!locationChoice || locationChoice === NONE || !Number.isFinite(locationId)) {
      setRacks([]);
      return undefined;
    }
    let alive = true;
    setLoadingRacks(true);
    api.getRacks(locationId, companyId)
      .then((res) => { if (alive) setRacks(res?.data || []); })
      .catch(() => { if (alive) setRacks([]); })
      .finally(() => { if (alive) setLoadingRacks(false); });
    return () => { alive = false; };
  }, [locationChoice, companyId]);

  /* Cambiar de ubicación invalida el rack elegido (era de otra sede). */
  const changeLocation = (value) => {
    setLocationChoice(value);
    setRackChoice(NO_CHANGE);
  };

  const racksEnabled = supportsRack && locationChoice !== NO_CHANGE && locationChoice !== NONE;

  const names = useMemo(
    () => rows.slice(0, 5).map((row) => row.name || row.code || `#${row.id}`),
    [rows],
  );
  const more = rows.length - names.length;

  const submit = async () => {
    const payload = {};
    if (locationChoice !== NO_CHANGE) {
      payload.location_id = locationChoice === NONE ? null : Number(locationChoice);
    }
    if (supportsRack && rackChoice !== NO_CHANGE) {
      payload.rack_id = rackChoice === NONE ? null : Number(rackChoice);
    }
    if (!Object.keys(payload).length) {
      setError(tr('bulk.nothing', 'Elige una ubicación o un rack.'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await api.bulkAssignPlacement(
        entity,
        rows.map((row) => row.id),
        payload,
        companyId,
      );
      onDone?.(result);
    } catch (e) {
      setError(e.message || tr('bulk.error', 'No se pudo aplicar el cambio'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={tr('bulk.title', 'Asignar ubicación y rack')}
      icon="📍"
      onClose={onClose}
      busy={saving}
      maxWidth={520}
      footer={(
        <>
          <button type="button" className="list-btn-secondary" onClick={onClose} disabled={saving}>
            {tr('action.cancel', 'Cancelar')}
          </button>
          <button type="button" className="list-btn-primary" onClick={submit} disabled={saving}>
            {saving ? tr('bulk.applying', 'Aplicando…') : tr('bulk.apply', 'Aplicar a los seleccionados')}
          </button>
        </>
      )}
    >
      <p className="list-hint" style={{ marginTop: 0 }}>
        <strong>{rows.length}</strong>{' '}
        {rows.length === 1
          ? tr('bulk.row_selected', 'elemento seleccionado')
          : tr('bulk.rows_selected', 'elementos seleccionados')}
        {names.length > 0 && (
          <> · {names.join(', ')}{more > 0 ? ` ${tr('bulk.and_more', 'y')} ${more} ${tr('bulk.more', 'más')}` : ''}</>
        )}
      </p>

      <label className="bulk-field">
        <span>{tr('bulk.location', 'Ubicación')}</span>
        <LocationTreeSelect
          locations={locations}
          value={locationChoice}
          onChange={(id) => changeLocation(id)}
          placeholder={tr('bulk.no_change', 'Sin cambios')}
          extraOptions={[
            { value: NO_CHANGE, label: tr('bulk.no_change', 'Sin cambios') },
            { value: NONE, label: tr('bulk.clear_location', 'Sin ubicación') },
          ]}
          disabled={saving}
        />
      </label>

      {supportsRack && (
        <label className="bulk-field">
          <span>{tr('bulk.rack', 'Rack')}</span>
          <select
            className="list-select"
            value={rackChoice}
            onChange={(event) => setRackChoice(event.target.value)}
            disabled={saving || !racksEnabled || loadingRacks}
          >
            <option value={NO_CHANGE}>{tr('bulk.no_change', 'Sin cambios')}</option>
            {racksEnabled && <option value={NONE}>{tr('bulk.clear_rack', 'Sin rack')}</option>}
            {racks.map((rack) => (
              <option key={rack.id} value={rack.id}>
                {rack.name}{rack.code ? ` (${rack.code})` : ''}
              </option>
            ))}
          </select>
          <small className="list-hint">
            {racksEnabled
              ? (loadingRacks ? tr('msg.loading', 'Cargando…') : tr('bulk.rack_hint', 'Racks de la ubicación elegida.'))
              : tr('bulk.rack_needs_location', 'Elige antes una ubicación para ver sus racks.')}
          </small>
        </label>
      )}

      {!supportsRack && (
        <p className="list-hint">
          {tr('bulk.no_rack_hint', 'Estos elementos no van montados en rack: solo se cambia la ubicación.')}
        </p>
      )}

      {error && <div className="alert alert-danger" role="alert" style={{ marginTop: 12 }}>{error}</div>}
    </Dialog>
  );
}
